import React from "react";
import { TrendingDown, PhoneCall, Gavel, AlertTriangle } from "lucide-react";

export default function EnemySection() {
  const panels = [
    {
      icon: TrendingDown,
      title: "YOUR CREDIT IS TAKING THE HIT.",
      titleEmphasis: true,
      desc: "That one collection can drag down your score, damage approvals, raise interest rates, hurt your chances at housing, and block the financing you need to move your life forward.",
      tagColor: "bg-brand-red/10 border-brand-red/20 text-brand-red"
    },
    {
      icon: PhoneCall,
      title: "DEBT COLLECTORS WON\u2019T STOP!",
      titleEmphasis: false,
      descParts: [
        "Calls. Letters. Texts. Settlement offers. Legal threats. Court papers. Everything about this system is designed to ",
        { text: "wear you down", emphasis: true },
        ", make you panic, and convince you that paying them is easier than fighting back."
      ],
      tagColor: "bg-brand-blue/10 border-brand-blue/20 text-brand-blue"
    },
    {
      icon: Gavel,
      title: "IGNORE IT — AND IT CAN GET UGLY FAST.",
      titleParts: [
        "IGNORE IT — AND ",
        { text: "IT CAN GET UGLY FAST.", emphasis: true }
      ],
      desc: "Once lawsuits, judgments, garnishments, or bank levies enter the picture, this is no longer just annoying — it becomes dangerous. Miss the wrong deadline, ignore the wrong notice, or stay silent too long, and the collector gains a serious advantage.",
      tagColor: "bg-brand-yellow/20 border-brand-yellow/30 text-brand-dark"
    }
  ];

  const companies = [
    "Portfolio Recovery",
    "Midland Credit",
    "Midland Funding",
    "LVNV Funding",
    "Resurgent Capital",
    "Credit Corp Solutions",
    "Radius Global",
    "Transworld Systems",
    "5,000+ Other Collection Agencies"
  ];

  const trappedThoughts = [
    "\u201cI can\u2019t afford to pay this.\u201d",
    "\u201cI don\u2019t know where to start.\u201d",
    "\u201cI already disputed it and nothing happened.\u201d",
    "\u201cWhat if they sue me?\u201d",
    "\u201cAm I stuck with this on my credit for years?\u201d",
    "\u201cI\u2019m tired of this hanging over my head.\u201d"
  ];

  return (
    <>
      {/* SECTION 1: Main Problem Section — darker cream */}
      <section id="the-enemy" className="bg-white pt-12 pb-14 md:pt-16 md:pb-16 border-b border-brand-border" data-testid="enemy-section-1">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">

          {/* Headline */}
          <h2 className="text-center font-display font-black uppercase text-brand-dark tracking-tight leading-[1.05] mb-6 max-w-[1000px] mx-auto">
            <span className="block text-3xl sm:text-5xl lg:text-[58px]">THEY&rsquo;RE COMING AFTER IT ALL!</span>
            <span className="block text-brand-red text-2xl sm:text-4xl lg:text-[38px] lg:whitespace-nowrap mt-2">YOUR PAYCHECK, YOUR CREDIT, YOUR PEACE OF MIND!</span>
          </h2>

          {/* Intro Copy */}
          <div className="max-w-[900px] mx-auto mb-10 text-center">
            <p className="font-sans font-extrabold text-brand-dark text-base sm:text-lg leading-relaxed">
              <span className="block">THEY WANT YOUR MONEY!</span>
              <span className="block lg:whitespace-nowrap mt-1">THEY&rsquo;RE COUNTING ON YOUR <span className="text-brand-red">FEAR, STRESS, SILENCE</span> <span className="text-brand-dark">AND</span> <span className="text-brand-red">CONFUSION</span> TO MAKE <span className="text-brand-red">YOU PAY!</span></span>
            </p>
          </div>

          {/* 3 Problem Panels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-[1120px] mx-auto items-stretch" data-testid="enemy-panels">
            {panels.map((panel, idx) => {
              const IconComponent = panel.icon;
              return (
                <div
                  key={idx}
                  className="bg-card border-2 border-brand-dark p-5 rounded-xl shadow-md flex flex-col h-full"
                >
                  <div className={`w-10 h-10 rounded-lg border-2 border-brand-dark ${panel.tagColor} shrink-0 shadow-sm flex items-center justify-center mb-3`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-black uppercase text-sm md:text-base leading-tight mb-2">
                    {panel.titleParts ? (
                      panel.titleParts.map((part, i) =>
                        typeof part === "string" ? (
                          <span key={i} className="text-brand-dark">{part}</span>
                        ) : (
                          <span key={i} className="text-brand-red">{part.text}</span>
                        )
                      )
                    ) : (
                      <span className={panel.titleEmphasis ? "text-brand-red" : "text-brand-dark"}>{panel.title}</span>
                    )}
                  </h3>
                  <p className="font-sans text-brand-dark/90 text-xs md:text-sm leading-relaxed">
                    {panel.descParts ? (
                      panel.descParts.map((part, i) =>
                        typeof part === "string" ? (
                          <span key={i}>{part}</span>
                        ) : (
                          <span key={i} className="text-brand-red font-extrabold">{part.text}</span>
                        )
                      )
                    ) : (
                      panel.desc
                    )}
                  </p>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* SECTION 2: Collectors / Pressure / Trap — lighter cream (ivory) */}
      <section className="bg-white pt-12 pb-14 md:pt-16 md:pb-16 border-b border-brand-border" data-testid="enemy-section-2">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">

          {/* Top Headline */}
          <h3 className="text-center font-display font-black uppercase text-brand-dark tracking-tight leading-[1.1] mb-3 max-w-[1000px] mx-auto">
            <span className="block text-xl sm:text-3xl lg:text-4xl">NO MATTER WHOSE TRYING TO COLLECT</span>
            <span className="block text-xl sm:text-3xl lg:text-4xl mt-1">THEY ALL WANT THE SAME THING: <span className="text-brand-red">YOUR MONEY!</span></span>
          </h3>

          {/* Collector Subline */}
          <p className="text-center font-display font-bold uppercase text-brand-dark/70 tracking-wide text-xs sm:text-sm mb-6 max-w-[820px] mx-auto">
            5,000+ COLLECTION AGENCIES. SAME SCARE TACTICS. SAME PRESSURE PLAYBOOK.
          </p>

          {/* Collector Names Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3 max-w-[1120px] mx-auto mb-10" data-testid="enemy-collector-grid">
            {companies.map((company, idx) => (
              <div
                key={idx}
                className="bg-card border-2 border-brand-dark px-4 py-3 rounded-full font-display font-black text-sm lg:text-base uppercase tracking-wide text-brand-dark shadow-sm text-center flex items-center justify-center lg:whitespace-nowrap leading-tight h-full"
              >
                {company}
              </div>
            ))}
          </div>

          {/* Pressure Copy */}
          <div className="max-w-[820px] mx-auto mb-10 space-y-4" data-testid="enemy-pressure-copy">
            <p className="text-center font-display font-black uppercase text-brand-dark text-sm md:text-lg lg:whitespace-nowrap tracking-wide bg-brand-yellow/25 border-2 border-brand-dark rounded-lg py-3 px-5 leading-snug">
              THEY&rsquo;RE COUNTING ON YOU TO PANIC, STRESS, PAY, OR DO NOTHING. ALL WRONG!
            </p>

            <p className="text-justify font-sans font-semibold text-brand-dark/90 text-sm md:text-base leading-relaxed">
              Their pressure is strategic and deliberate. Debt collection is a multi-billion-dollar industry. The calls. The letters. The negative credit reporting. The legal threats. They aggressively push fear, stress, confusion - while counting on your silence to make their business more profitable.
            </p>

            <p className="text-justify font-sans font-extrabold text-brand-dark text-sm md:text-base leading-relaxed">
              DEBT BUYERS PURCHASE CHARGED OFF ACCOUNTS FOR ABOUT 4 CENTS ON THE DOLLAR - THEN COME AFTER YOU FOR THE FULL BALANCE, BEFORE YOU&rsquo;VE SEEN ACCOUNT LEVEL PROOF OR THEIR LEGAL RIGHT TO COLLECT.
            </p>
          </div>

          {/* Emotional Trap Block */}
          <div className="max-w-[760px] mx-auto mb-10 text-center" data-testid="enemy-emotional-close">
            <h4 className="font-display font-black uppercase text-brand-dark text-2xl md:text-3xl tracking-tight mb-5">
              YOU DO NOT HAVE TO STAY STUCK OR FEEL TRAPPED.
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-[700px] mx-auto text-left">
              {trappedThoughts.map((thought, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-brand-cream/50 border border-brand-border rounded-lg px-3 py-2.5">
                  <AlertTriangle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                  <span className="font-sans font-semibold text-brand-dark/90 text-xs md:text-sm italic">{thought}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Close of Problem Section — Black Banner */}
          <div className="max-w-[900px] mx-auto bg-[#111111] rounded-xl py-6 px-6 md:px-8 text-center" data-testid="enemy-close">
            <p className="font-display font-black uppercase text-brand-yellow text-base md:text-xl tracking-tight leading-snug mb-2">
              &ldquo;THEIR EASIEST WIN IS THE FIGHT YOU NEVER SHOW UP FOR.&rdquo;
            </p>
            <p className="font-display font-black uppercase text-white text-base sm:text-lg lg:text-xl tracking-tight leading-snug">
              STOP STRESSING. STOP IGNORING. STOP THE SILENCE. <span className="text-brand-red">DEMAND THEY PROVE IT!</span>
            </p>
          </div>

        </div>
      </section>
    </>
  );
}
